import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Save, Gift, Users, Trophy } from "lucide-react";
import PromotionalCoupons from "@/components/admin/PromotionalCoupons";
import InstagramRewardsSettingsCard from "@/components/admin/InstagramRewardsSettingsCard";
import InstagramInstructionsCard from "@/components/admin/InstagramInstructionsCard";

interface RewardTier {
  referrals_needed: number;
  reward_description: string;
}

const Rewards = () => {
  const [enabled, setEnabled] = useState(false);
  const [tiers, setTiers] = useState<RewardTier[]>([
    { referrals_needed: 3, reward_description: "10% off next service" },
    { referrals_needed: 5, reward_description: "Free nail art upgrade" },
    { referrals_needed: 10, reward_description: "Free full service" },
  ]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [referralStats, setReferralStats] = useState<{ referrer: string; count: number }[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await supabase.from("site_settings").select("key, value");
      if (data) {
        data.forEach((s: any) => {
          if (s.key === "reward_program_enabled") setEnabled(s.value === "true");
          if (s.key === "reward_tiers") {
            try {
              setTiers(JSON.parse(s.value));
            } catch {}
          }
        });
      }

      // Fetch referral stats
      const { data: entries } = await supabase
        .from("referral_entries")
        .select("referrer_user_id");

      if (entries) {
        const counts: Record<string, number> = {};
        entries.forEach((e: any) => {
          counts[e.referrer_user_id] = (counts[e.referrer_user_id] || 0) + 1;
        });

        const userIds = Object.keys(counts);
        if (userIds.length > 0) {
          const { data: profiles } = await supabase
            .from("profiles")
            .select("user_id, full_name")
            .in("user_id", userIds);

          const nameMap: Record<string, string> = {};
          profiles?.forEach((p: any) => { nameMap[p.user_id] = p.full_name || "Unnamed"; });

          setReferralStats(
            Object.entries(counts)
              .map(([uid, count]) => ({ referrer: nameMap[uid] || uid.slice(0, 8), count }))
              .sort((a, b) => b.count - a.count)
          );
        }
      }

      setLoading(false);
    };
    fetchSettings();
  }, []);

  const upsertSetting = async (key: string, value: string) => {
    const { data: existing } = await supabase
      .from("site_settings")
      .select("id")
      .eq("key", key)
      .maybeSingle();

    if (existing) {
      await supabase.from("site_settings").update({ value, updated_at: new Date().toISOString() }).eq("key", key);
    } else {
      await supabase.from("site_settings").insert({ key, value });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        upsertSetting("reward_program_enabled", enabled ? "true" : "false"),
        upsertSetting("reward_tiers", JSON.stringify(tiers)),
      ]);
      toast({ title: "Saved", description: "Rewards settings updated" });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setSaving(false);
  };

  const updateTier = (index: number, field: keyof RewardTier, value: string | number) => {
    setTiers((prev) => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
  };

  const addTier = () => {
    setTiers((prev) => [...prev, { referrals_needed: 1, reward_description: "" }]);
  };

  const removeTier = (index: number) => {
    setTiers((prev) => prev.filter((_, i) => i !== index));
  };

  if (loading) {
    return <div className="text-muted-foreground text-sm">Loading rewards settings…</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl font-bold italic">Coupons &amp; Rewards</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage coupons, referral rewards, and tiers</p>
      </div>

      {/* Enable toggle */}
      <Card className="bg-card border-border/50 max-w-xl">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <Gift size={16} />
            Program Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 border border-border/50 bg-background">
            <div>
              <p className="text-sm font-medium">Enable Rewards Program</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {enabled
                  ? "Clients earn rewards by referring others"
                  : "Rewards program is currently disabled"}
              </p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} />
          </div>
        </CardContent>
      </Card>

      {/* Reward tiers */}
      <Card className="bg-card border-border/50 max-w-xl">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <Trophy size={16} />
            Reward Tiers
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground text-sm font-light">
            Define how many referrals are needed for each reward level. In addition, every successful referral booking automatically earns the referrer a <strong className="text-primary">$5 credit</strong>.
          </p>
          {/* Automatic $5 credit - always shown */}
          <div className="p-4 border border-primary/30 bg-primary/10 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Every successful referral</p>
              <p className="text-xs text-muted-foreground mt-0.5">Automatic — no configuration needed</p>
            </div>
            <span className="text-primary font-bold text-sm">$5 credit</span>
          </div>
          {tiers.map((tier, i) => (
            <div key={i} className="flex items-end gap-3 p-4 border border-border/50 bg-background">
              <div className="flex-shrink-0 w-24">
                <Label className="text-xs tracking-wide uppercase text-muted-foreground">Referrals</Label>
                <Input
                  type="number"
                  min={1}
                  value={tier.referrals_needed}
                  onChange={(e) => updateTier(i, "referrals_needed", parseInt(e.target.value) || 1)}
                  className="bg-card border-border/50 mt-1"
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs tracking-wide uppercase text-muted-foreground">Reward</Label>
                <Input
                  value={tier.reward_description}
                  onChange={(e) => updateTier(i, "reward_description", e.target.value)}
                  placeholder="e.g. 10% off next service"
                  className="bg-card border-border/50 mt-1"
                />
              </div>
              {tiers.length > 1 && (
                <Button variant="ghost" size="sm" onClick={() => removeTier(i)} className="text-destructive text-xs shrink-0">
                  Remove
                </Button>
              )}
            </div>
          ))}
          <Button variant="outline" onClick={addTier} className="text-xs tracking-widest uppercase border-primary/30 hover:bg-primary/10">
            + Add Tier
          </Button>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="text-xs tracking-widest uppercase">
        <Save size={14} className="mr-2" />
        {saving ? "Saving…" : "Save Rewards Settings"}
      </Button>

      {/* Promotional coupons */}
      <PromotionalCoupons />

      {/* Instagram link approval (moved from Admin Settings) */}
      <InstagramRewardsSettingsCard />

      {/* Send Instagram posting instructions to clients */}
      <InstagramInstructionsCard />

      {/* Referral leaderboard */}
      <Card className="bg-card border-border/50 max-w-xl">
        <CardHeader>
          <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
            <Users size={16} />
            Referral Leaderboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          {referralStats.length === 0 ? (
            <p className="text-muted-foreground text-sm font-light">No referrals yet.</p>
          ) : (
            <div className="space-y-2">
              {referralStats.map((stat, i) => (
                <div key={i} className="flex items-center justify-between p-3 border border-border/30 bg-background">
                  <span className="text-sm font-medium">{stat.referrer}</span>
                  <span className="text-xs text-muted-foreground">
                    {stat.count} referral{stat.count !== 1 ? "s" : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Rewards;
